// NodeContextMenu — right-click a node in any graph editor.
//
// Round-2 UI note: "Right click on node menu broken on cascade - arrange takes
// over if you mouse down over it on your way to delete".
//
// The rows are Duplicate / Arrange / Delete stacked vertically, so a pointer
// heading for Delete MUST cross Arrange. Arrange opened on bare `onMouseEnter`
// with no delay AND rendered as a REPLACEMENT panel — the whole menu body was
// `showArrange ? <ArrangeMenuItems/> : <>rows</>`, the panel even resized itself
// from 150 to 210px. So merely passing over Arrange destroyed Delete and put an
// Align row at that y, which is why a press "ran arrange". The only way back was
// the flyout's own "‹ Back" row, because once open the Arrange row no longer
// existed to fire an onMouseLeave.
//
// The fix is structural, not a timing tweak: a FLYOUT beside the menu, so the
// parent list never goes away and Delete stays where the user is aiming. That
// pattern already exists here — MenuSubmenu + useOverlayAnchor + isInsideSubmenu,
// as used by contentBrowser/NewAssetMenu — including the two things a portaled
// flyout needs: it carries the row's hover handlers itself, and the parent only
// dismisses it when the pointer leaves for somewhere that is NOT the flyout.
//
// A short hover-intent delay sits on top. It is the second layer, not the fix:
// with the flyout in place a fast diagonal traverse merely flashes it, and the
// delay stops even that.
//
// This file also collapses a duplicate. The menu existed twice, byte-identical,
// in BlueprintEditor.jsx and EventGraph.jsx — so the body/face and event menus
// could drift, and this bug had to be fixed twice or not at all.

import { useCallback, useEffect, useRef, useState } from 'react'
import { formatKeys, getShortcut } from '../../lib/shortcutRegistry'
import AnchoredOverlay from '../AnchoredOverlay'
import MenuSubmenu, { isInsideSubmenu } from '../MenuSubmenu'
import { ArrangeMenuItems } from './ArrangeMenuItems'

// Long enough that crossing the row on the way past does not open it, short
// enough that deliberately resting on it feels immediate.
const HOVER_INTENT_MS = 120

const SUB_W = 210

const row = {
  padding: '7px 12px', cursor: 'pointer',
  color: 'var(--m-fg-2, #aaa)',
}
const hover = (on) => (e) => {
  e.currentTarget.style.background = on ? 'var(--m-panel-hi, #1f1f1f)' : 'transparent'
}

export default function NodeContextMenu({
  screenX, screenY, nodeId, deletable, onDelete, onDuplicate, onArrange, onCopy, onCut, onClose,
}) {
  const [subOpen, setSubOpen] = useState(false)
  const arrangeRef = useRef(null)
  const timerRef = useRef(null)

  const cancelTimer = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
  }, [])
  useEffect(() => cancelTimer, [cancelTimer])

  // Open on INTENT, not on entry. Cancelled by leaving, so a pointer merely
  // travelling down to Delete never opens it at all.
  const armOpen = useCallback(() => {
    cancelTimer()
    timerRef.current = setTimeout(() => setSubOpen(true), HOVER_INTENT_MS)
  }, [cancelTimer])

  // `submenuWidth` + a function child is AnchoredOverlay's own API for this:
  // it runs useOverlayAnchor internally, with the measuring ref attached, and
  // hands back `flip` for the side the flyout should open on. Calling the hook
  // separately out here anchors the menu twice from an unmeasured element.
  return (
    <AnchoredOverlay
      x={screenX} y={screenY} margin={8} submenuWidth={SUB_W}
      data-testid="node-context-menu"
      onMouseDown={(e) => e.stopPropagation()}
      // The editor wrapper closes both menus on any click (BlueprintEditor /
      // EventGraph `onClick={() => { closeMenu(); closeNodeMenu() }}`). The old
      // menu stopped only mousedown, so clicking Arrange unmounted the whole
      // menu — its onClick open was dead code and hover was the only way in.
      // The add-node palette has always stopped click for exactly this reason.
      onClick={(e) => e.stopPropagation()}
      // Only when the pointer leaves for somewhere that is NOT the flyout: it is
      // portaled to <body>, so moving onto it LEAVES this element, and without
      // the check the list closes on the way to the very rows it opened.
      onMouseLeave={(e) => {
        if (!isInsideSubmenu(e.relatedTarget)) { cancelTimer(); setSubOpen(false) }
      }}
      style={{
        width: 150,
        background: 'var(--m-panel, #141414)', border: '1px solid var(--m-line, #303030)',
        borderRadius: 5, boxShadow: '0 4px 20px rgba(0,0,0,0.7)',
        zIndex: 1000, fontFamily: 'var(--m-font)', fontSize: 11, overflow: 'visible',
      }}
    >
      {({ flip }) => (<>
      <div
        data-testid="node-menu-duplicate"
        onClick={() => { onDuplicate(nodeId); onClose() }}
        onMouseEnter={(e) => { hover(true)(e); cancelTimer(); setSubOpen(false) }}
        onMouseLeave={hover(false)}
        style={{ ...row, borderBottom: '1px solid var(--m-line, #1e1e1e)' }}
      >Duplicate</div>

      {/* Copy / Cut act on the whole SELECTION, not on the node the menu was
          opened over — that is what the chords do, and a menu row that quietly
          meant something narrower would be the surprise. Omitted entirely when
          the host publishes no handler (a read-only canvas), never rendered
          as a dead row. */}
      {onCopy && (
        <div
          data-testid="node-menu-copy"
          onClick={() => { onCopy(); onClose() }}
          onMouseEnter={(e) => { hover(true)(e); cancelTimer(); setSubOpen(false) }}
          onMouseLeave={hover(false)}
          style={{ ...row, display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--m-line, #1e1e1e)' }}
        ><span>Copy</span><span style={{ opacity: .45 }}>{formatKeys(getShortcut('graph-copy'))}</span></div>
      )}
      {onCut && (
        <div
          data-testid="node-menu-cut"
          onClick={() => { onCut(); onClose() }}
          onMouseEnter={(e) => { hover(true)(e); cancelTimer(); setSubOpen(false) }}
          onMouseLeave={hover(false)}
          style={{ ...row, display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--m-line, #1e1e1e)' }}
        ><span>Cut</span><span style={{ opacity: .45 }}>{formatKeys(getShortcut('graph-cut'))}</span></div>
      )}

      <div
        ref={arrangeRef}
        data-testid="node-menu-arrange"
        // Click still opens it immediately — someone who aimed at it should not
        // wait out a delay meant for someone who did not.
        onClick={() => { cancelTimer(); setSubOpen(true) }}
        onMouseEnter={(e) => { hover(true)(e); armOpen() }}
        onMouseLeave={(e) => { hover(false)(e); cancelTimer() }}
        style={{
          ...row, display: 'flex', justifyContent: 'space-between',
          borderBottom: '1px solid var(--m-line, #1e1e1e)',
        }}
      ><span>Arrange</span><span style={{ color: 'var(--m-fg-3)' }}>›</span></div>

      {deletable !== false && (
        <div
          data-testid="node-menu-delete"
          onClick={() => { onDelete(nodeId); onClose() }}
          // Reaching Delete closes the flyout rather than leaving it hanging
          // over the canvas — and cancels any armed open from the row above.
          onMouseEnter={(e) => { hover(true)(e); cancelTimer(); setSubOpen(false) }}
          onMouseLeave={hover(false)}
          style={{ ...row, color: 'var(--m-danger, #e06060)' }}
        >Delete</div>
      )}

      <MenuSubmenu
        rowRef={arrangeRef}
        open={subOpen}
        minWidth={SUB_W}
        flip={flip}
        onMouseEnter={cancelTimer}
        onMouseLeave={(e) => { if (!isInsideSubmenu(e.relatedTarget)) setSubOpen(false) }}
        style={{
          background: 'var(--m-panel, #141414)', border: '1px solid var(--m-line, #303030)',
          borderRadius: 5, boxShadow: '0 4px 20px rgba(0,0,0,0.7)',
          fontFamily: 'var(--m-font)', fontSize: 11,
        }}
      >
        {/* The rows are unchanged and still shared; the flyout just drops the
            Back row, which had nothing left to go back to. */}
        <ArrangeMenuItems onRun={(op) => { onArrange?.(op); onClose() }} />
      </MenuSubmenu>
      </>)}
    </AnchoredOverlay>
  )
}
